import { Component, inject, signal, ChangeDetectionStrategy, effect } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";

import {
  UsersGroupsControllerComponent
} from "../../../../../shared/users-groups-controller/users-groups-controller.component";
import { WorkflowBuilderService } from "../services/workflow-builder.service";
import { MarketplaceController } from "../../../../../shared/marketplace-controller/marketplace-controller";
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '../../../../../services/api.service';
import { ConversationTypeEnum } from '../../../../../pages/conversation-starter/conversation-type-enum';
import {
  PendingAccessUsersDialogComponent
} from '../../../../../shared/pending-access-users-dialog/pending-access-users-dialog.component';
import { INotifiedUser } from '../../../../../interfaces/i-notification';
import { IUser } from '../../../../../interfaces/i-user-profile';

/**
 * WorkflowSecurityComponent manages the security step of the workflow builder.
 * It handles the form for workflow owners, users, and groups, and synchronizes security data with the workflow service.
 */
@Component({
  selector: 'workflow-security',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UsersGroupsControllerComponent,
    MarketplaceController,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './workflow-security.component.html',
  styleUrl: './workflow-security.component.scss'
})
export class WorkflowSecurityComponent {
  securityForm!: FormGroup;

  /** Users who have requested access and are awaiting approval. */
  readonly pendingUsers = signal<INotifiedUser[]>([]);
  /** True while the pending-users API call is in flight. */
  readonly pendingUsersLoading = signal(false);

  #formBuilder = inject(FormBuilder);
  #workflowBuilderService = inject(WorkflowBuilderService);
  #apiService = inject(ApiService);
  #dialog = inject(MatDialog);

  /** Track the last workflow id we fetched pending users for to avoid duplicate calls. */
  #lastFetchedId = '';

  constructor() {
    this.securityForm = this.#formBuilder.group({
      owners: new FormControl([], [Validators.required]),
      users: new FormControl([]),
      groups: new FormControl([]),
      isAdvertised: new FormControl(false),
      isAutoApproved: new FormControl(false),
      notifyAutoApproval: new FormControl(false)
    });

    // Seed owners once from the current snapshot so the child UsersGroupsController
    // can display the existing owners immediately. We do NOT patch owners inside the
    // effect below because UsersGroupsController is authoritative for owner changes.
    const snapshot = this.#workflowBuilderService.workflow();
    const snapshotOwners = snapshot?.details?.owners ?? [];
    if (snapshotOwners.length > 0) {
      this.securityForm.get('owners')?.setValue(snapshotOwners, { emitEvent: false });
    }

    // React to the workflow signal to keep non-owner fields and the
    // pending-users call in sync, regardless of whether the user navigated
    // directly to this step or arrived via the stepper.
    effect(() => {
      const workflow = this.#workflowBuilderService.workflow();
      const details = workflow?.details;

      // Deliberately exclude 'owners' — the child UsersGroupsController owns that
      // control and writes back via onOwnersChanged(). Patching it here would
      // overwrite the user's selection every time the signal updates.
      this.securityForm.patchValue({
        users: details?.users ?? [],
        groups: details?.groups ?? [],
        isAdvertised: details?.isAdvertised ?? false,
        isAutoApproved: details?.isAutoApproved ?? false,
        notifyAutoApproval: details?.notifyAutoApproval ?? false
      }, { emitEvent: false });

      // Re-emit isAdvertised value so the MarketplaceController updates its
      // `showMarketplaceSettings` signal when the API reports isAdvertised=true.
      const advControl = this.securityForm.get('isAdvertised');
      if (advControl) {
        advControl.setValue(advControl.value, { emitEvent: true });
      }

      const workflowId = workflow?.id;
      if (workflowId && workflowId !== this.#lastFetchedId) {
        this.#lastFetchedId = workflowId;
        // Seed owners from the newly-loaded workflow (edit/clone scenario).
        if (details?.owners?.length) {
          this.securityForm.get('owners')?.setValue(details.owners, { emitEvent: false });
        }
        this.pendingUsersLoading.set(true);
        this.#apiService.getPendingUsers(workflowId, ConversationTypeEnum.Workflow).subscribe({
          next: (users: INotifiedUser[]) => this.pendingUsers.set(users ?? []),
          error: () => this.pendingUsers.set([]),
          complete: () => this.pendingUsersLoading.set(false)
        });
      }
    });
  }

  /**
   * Opens the pending access users dialog.
   * Any users confirmed in the dialog are merged into the `users` form control.
   */
  openPendingUsersDialog(): void {
    const dialogRef = this.#dialog.open(PendingAccessUsersDialogComponent, {
      data: { pendingUsers: this.pendingUsers() }
    });

    dialogRef.afterClosed().subscribe((selectedUsers: IUser[] | undefined) => {
      if (!selectedUsers?.length) return;
      const current: IUser[] = this.securityForm.get('users')?.value ?? [];
      const merged = [
        ...current,
        ...selectedUsers.filter(s => !current.some(c => c.userId === s.userId))
      ];
      this.securityForm.get('users')?.setValue(merged);
      this.updateWorkflow();
    });
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    // Check if the form in the UsersGroupsControllerComponent is valid and dirty (i.e., has been modified)
    if (!this.securityForm.valid) {
      // Mark all controls in the UsersGroupsControllerComponent as touched to trigger validation errors in the UI
      Object.values(this.securityForm.controls).forEach(control => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });
      return false;
    }
    return this.securityForm.valid;
  }

  /**
   * Marks all controls in the security form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the WorkflowBuilderComponent.
   */
  markFormAsTouched(): void {
    this.securityForm.markAllAsTouched();
  }

  /**
   * Resets the security form to its initial state, clearing all values and validation states.
   */
  resetForm() {
    this.securityForm.reset();
    this.securityForm.markAsPristine();
    this.securityForm.markAsUntouched();
  }

  /**
   * Updates the workflow service with the current values from the security form.
   * Synchronizes owners, users, and groups with the workflow state.
   */
  updateWorkflow() {
    const currentWorkflow = this.#workflowBuilderService.workflow();
    const updatedWorkflow = {
      ...currentWorkflow,
      details: {
        ...currentWorkflow.details,
        ...this.securityForm.value
      }
    };
    this.#workflowBuilderService.workflow.set(updatedWorkflow);
  }

  /**
   * Handler for the ownersChanged event emitted by the UsersGroupsControllerComponent.
   * Called whenever the owners control value changes in the child component.
   * Updates the workflow state with the latest security form values.
   */
  onOwnersChanged() {
    this.updateWorkflow();
  }
}
